// Weapons: rifle + sniper + the monocular slot. Two-handed grip steadies aim.
// All numbers come from tunables.weapons (live-pushed).
// VR: the sniper has a real render-to-texture scope (POP:1 style — no FOV
// change, so no nausea); the monocular slot has no model (owner 2026-11-01:
// no monocular / binocular in VR) — the right stick's zoom is the HEAD zoom
// window (monoRt posed from the head, a plain 1.6:1 rectangle ahead, ONLY the
// desktop view's labels on it: scopehud.js paintZoomWindow).
// Desktop + phone: no optic model at all — the one view zooms (viewzoom.js),
// and from the optic band on (fov <= monocular.fov_deg) the MAIN render pass
// looks through monoCam (poseMonoFull/renderMonoFull) with the optic rules:
// dehaze, clarity, the focus wedge, the depth the labels test against.
// Optic clarity: the VR zoom window shows its RT through an unsharp mask + a
// local-contrast term (OPTIC_GLSL); the band gets the same kernel through an
// RT + fullscreen quad on the tiers in optic.sharpen_tiers.
// Label line of sight: both optic targets keep their depth, packed and read
// back after each render; opticDepth answers gazetteer.inView.
import * as THREE from 'three';
import { setHazeMul } from './sky.js';
import { detailTier } from './terrain.js';
import { paintZoomWindow } from './scopehud.js';
import { zoomRtSize, zoomEvery } from './device.js';

export class Weapons {
  constructor(renderer, scene, camera) {
    this.renderer = renderer;
    this.scene = scene;
    this.current = 'rifle';
    this.lastFireAt = 0;
    this.swayT = 0;
    this.twoHanded = false;

    this.rifle = makeRifle();
    this.sniper = makeSniper();
    this.monocular = makeMonocular();
    this.group = new THREE.Group();
    this.group.add(this.rifle, this.sniper, this.monocular);
    this.sniper.visible = false;
    this.monocular.visible = false;

    // VR sniper scope RT (the lens texture; desktop/phone aim through the
    // view's optic band instead and never render it)
    this.rt = new THREE.WebGLRenderTarget(512, 512, { samples: 0 });
    this.scopeCam = new THREE.PerspectiveCamera(6.5, 1, 0.6, 130000);
    this.sniper.userData.lens.material.map = this.rt.texture;
    this.sniper.userData.lens.material.needsUpdate = true;
    this.vrScoping = false;
    this._rtFrame = 0;

    // the optic's RT + camera (1.6:1 aspect, no sway). VR renders the RT for
    // the head zoom window; desktop/phone use monoCam alone, as the band's camera.
    // High tier (desktop GPU): MSAA + half float, so snow keeps its range
    // into the display curve and the sky has no 8-bit linear banding; else
    // the cheap 8-bit single-sample target. The tier is fixed at boot
    // (initDetailQuality runs before Weapons is built).
    this._tier = detailTier();
    const ext = renderer.extensions;
    this._hdrType = ext.has('EXT_color_buffer_float') || ext.has('EXT_color_buffer_half_float')
      ? THREE.HalfFloatType : THREE.UnsignedByteType;
    const monoSamples = this._tier === 2 ? 4 : 0;
    // the RT's size × the class draw rule (device.js CLASS_DRAW zoom_rt; quest
    // 0.75 = 768×480, the overlay canvas + the label space stay 1024×640, the
    // lens texel and the depth pack read the RT's own size)
    const [mw, mh] = zoomRtSize();
    this.monoRt = new THREE.WebGLRenderTarget(mw, mh, {
      samples: monoSamples, type: monoSamples ? this._hdrType : THREE.UnsignedByteType,
      depthTexture: _opticDepthTex(renderer, monoSamples, mw, mh),
    });
    // the VR head zoom window renders its RT every monoEvery-th frame (a
    // second scene render; CLASS_DRAW zoom_every: quest every 3rd)
    this.monoEvery = zoomEvery();
    this.monoCam = new THREE.PerspectiveCamera(10, 1024 / 640, 0.6, 130000);
    // the labels' line of sight (opticDepth): every optic render's depth,
    // packed as linear view depth, one texel per 4×4 optic px, read back
    // async; `_depth` = the last snapshot that landed, `_dSnap` the two
    // alternating buffers (one in flight at most, `_dBusy`)
    this._dPack = _makeDepthPack();
    this._dSnap = [_depthSnap(), _depthSnap()];
    this._dNext = 0;
    this._dBusy = false;
    this._depth = null;
    this._dLast = -1e9;      // last optic render (_packDepth), ms
    this._dGen = 0;          // optic session: bumped when the optic was off
    // gazetteer.inView's depthAt (main.js _monoLabels)
    this.depthAt = (sx, sy, i, j) => this.opticDepth(sx, sy, i, j);
    // optic clarity: strengths shared (same uniform objects) by the VR zoom
    // window's lens and the band's fullscreen quad, so applyTunables retunes
    // both live. _clarity = the tuned strengths; the quad's live values are
    // those times the band ramp (setBand), the lens always runs them at full
    // strength.
    this._optic = {
      uOptSharpen: { value: 0.6 },
      uOptClarity: { value: 0.35 },
      uOptClarityR: { value: 6 },
    };
    this._clarity = { sharpen: 0.6, clarity: 0.35 };
    this._sharpenTiers = [2];
    // the band with the kernel: scene → _fullRt (allocated on first use,
    // drawing-buffer size) → this quad → canvas. Its own uniform objects for
    // the strengths (ramped), the radius shared with the lens.
    this._fullOptic = { uOptSharpen: { value: 0.6 }, uOptClarity: { value: 0.35 },
      uOptClarityR: this._optic.uOptClarityR };
    this._full = _makeFullQuad(this._fullOptic);
    this._fullRt = null;
    this._monoFrame = 0;
    this.monoZoom = 1.0;       // the band's optic zoom: 1.0 = base FOV, monoMaxZoom = max (setBand)
    this.monoBaseFov = 10;     // base vertical FOV degrees
    this.monoMaxZoom = 2.0;    // tunables.weapons.monocular.max_zoom
    // VR head zoom (the right stick's Y, main.js): the same optic posed from
    // the HEAD and shown as a window in front of the eyes (WebXR fixes the
    // eye projection, so the view itself cannot narrow). The window spans
    // headVfov (tunables.view.vr_zoom_window_deg) at 1×, where its picture is
    // the naked eye's; Z = headVfov / monoCam.fov runs 1 → headZMax (the
    // optic's narrowest, fov_deg / max_zoom). Hidden at 1×. Its overlay
    // (the desktop view HUD: labels, compass, reticle, range, MAG) is drawn
    // into _winCanvas by drawLabels.
    this.headVfov = 40;
    this.headRate = 1.6;       // ln Z per second at full deflection (view.vr_zoom_rate)
    this.headZ = 1;
    this.headZTarget = 1;
    this._zFrom = null;        // the zoom a stick gesture started from (null: none running)
    this._head = null;         // { win (group, a child of the head), lens, overlay } (lazy)
    this._winCanvas = document.createElement('canvas');
    this._winCanvas.width = 1024;
    this._winCanvas.height = 640;
    this._winTex = new THREE.CanvasTexture(this._winCanvas);
    // desktop/phone: this frame renders through monoCam (setBand, main.js).
    // Off = monoCam is not posed and nothing optic runs.
    this.band = false;
    this._bandRamp = 1;        // 0 at band entry → 1 a couple of notches in
    // last poseMonoFull timestamp: a gap means the band was left in between,
    // so the next pose snaps instead of easing from a stale one
    this._monoFullAt = -1e9;
    // freeze-frame (the inspect card, main.js → inspect.js): while true the
    // optic HOLDS its picture — VR zoom window: no re-render, no label redraw
    // (the RT + overlay keep the snapshot, the depth snapshot ages out as
    // usual); the band: monoCam holds its pose and zoom (the main pass still
    // renders through it), main.js holds the labels
    this.monoFreeze = false;
    // optical dehaze while glassing: haze multiplier applied to the backdrop
    // haze AND the scene fog during an optic render only — through the
    // glass, far peaks read clearer than naked-eye. The band ramps it in.
    this.monoClear = 0.12;
    // scope focus streaming (focus.js): main.js hands us focus.setPass, which
    // arms the terrain shader's wedge term. It is set ONLY around an optic
    // render — the naked-eye pass must never see it.
    this.opticPass = null;
  }

  // focus-only terrain/building tiles live on their own layer so that the main
  // pass is byte-identical to a build without the feature. Only the two optic
  // cameras are ever told about it; `camera` (and the XR eye cameras, which
  // use layers 1/2) keep their default mask.
  setFocusLayer(layer) {
    if (layer == null) return;
    this.monoCam.layers.enable(layer);
    this.scopeCam.layers.enable(layer);
  }

  applyTunables(t) {
    const w = t.weapons;
    this.scopeCam.fov = w.sniper.scope_fov_deg;
    this.scopeCam.updateProjectionMatrix();
    const px = w.sniper.scope_rt_px || 512;
    if (this.rt.width !== px) this.rt.setSize(px, px);
    const hz = t.world && t.world.haze;
    if (hz && hz.mono_clear != null) this.monoClear = hz.mono_clear;
    const v = t.view || {};
    if (v.vr_zoom_window_deg > 0 && v.vr_zoom_window_deg !== this.headVfov) {
      this.headVfov = v.vr_zoom_window_deg;
      if (this._head) this._sizeHead();
    }
    if (v.vr_zoom_rate > 0) this.headRate = v.vr_zoom_rate;
    if (w.monocular) {
      if (w.monocular.fov_deg != null) {
        this.monoBaseFov = w.monocular.fov_deg;
        this.monoCam.fov = this.monoBaseFov / this.monoZoom;
        this.monoCam.updateProjectionMatrix();
      }
      if (w.monocular.max_zoom != null) this.monoMaxZoom = w.monocular.max_zoom;
      this.headZTarget = Math.min(this.headZTarget, this.headZMax);
      const o = w.monocular.optic;
      if (o) {
        if (o.sharpen != null) this._optic.uOptSharpen.value = this._clarity.sharpen = o.sharpen;
        if (o.clarity != null) this._optic.uOptClarity.value = this._clarity.clarity = o.clarity;
        if (o.clarity_radius_px != null) this._optic.uOptClarityR.value = o.clarity_radius_px;
        if (o.sharpen_tiers) this._sharpenTiers = o.sharpen_tiers;
        this._applyRamp();
      }
    }
  }

  // the monocular slot is in hand (VR: the head zoom window + the trigger's
  // card; desktop/phone: the act slot — LMB / tap inspects)
  get monoActive() { return this.current === 'monocular'; }

  // desktop/phone, every frame (main.js): does this frame render through
  // monoCam (the optic band), at which zoom (bandFov / fov, 1..max_zoom),
  // with which ramp (0 at band entry → 1: dehaze + clarity fade in)?
  setBand(on, monoZoom = 1, ramp = 1) {
    if (!on) {
      if (this.band) this._monoFullAt = -1e9;   // the next entry snaps
      this.band = false;
      return;
    }
    this.band = true;
    if (!this.monoFreeze) {
      this.monoZoom = Math.max(1, Math.min(this.monoMaxZoom, monoZoom));
      this.monoCam.fov = this.monoBaseFov / this.monoZoom;
    }
    this._bandRamp = Math.max(0, Math.min(1, ramp));
    this._applyRamp();
  }

  _applyRamp() {
    const k = this._bandRamp;
    this._fullOptic.uOptSharpen.value = this._clarity.sharpen * k;
    this._fullOptic.uOptClarity.value = this._clarity.clarity * k;
  }

  // the band's haze multiplier: naked-eye (1) at entry → monoClear
  _bandClear() { return 1 + (this.monoClear - 1) * this._bandRamp; }

  switchTo(name) {
    if (name === this.current) return;
    this.current = name;
    this.rifle.visible = name === 'rifle';
    this.sniper.visible = name === 'sniper';
    this.monocular.visible = name === 'monocular';
    // reset zoom when switching away
    if (name !== 'monocular') {
      this.monoZoom = 1.0;
      this.monoCam.fov = this.monoBaseFov;
      this.monoCam.updateProjectionMatrix();
    }
  }

  toggle() {
    const order = ['rifle', 'sniper', 'monocular'];
    this.switchTo(order[(order.indexOf(this.current) + 1) % 3]);
  }

  get model() {
    return this.current === 'rifle' ? this.rifle
      : this.current === 'sniper' ? this.sniper : this.monocular;
  }
  get muzzle() { return this.model.userData.muzzle; }

  // no reloading — ammo is infinite, only the rate-of-fire gates a shot
  canFire(tun, now) {
    if (this.current === 'monocular') return false; // observational only
    const w = tun.weapons[this.current];
    return now - this.lastFireAt >= 60000 / w.rpm;
  }

  // returns the spread-adjusted world dir
  fireDir(tun, now, aimObj) {
    const w = tun.weapons[this.current];
    this.lastFireAt = now;
    const d = new THREE.Vector3(0, 0, -1)
      .applyQuaternion(aimObj.getWorldQuaternion(new THREE.Quaternion()));
    let spread = w.spread_deg * Math.PI / 180;
    if (this.twoHanded) spread *= (w.two_hand_spread_mult || 1);
    if (spread > 0) {
      // scatter in the plane PERPENDICULAR to the barrel — nudging world x/y
      // instead skews shots that aim steeply up/down (routine in VR)
      const up = Math.abs(d.y) < 0.99 ? _spreadUp.set(0, 1, 0) : _spreadUp.set(1, 0, 0);
      const s1 = _spread1.crossVectors(d, up).normalize();
      const s2 = _spread2.crossVectors(d, s1);
      d.addScaledVector(s1, (Math.random() - 0.5) * spread)
        .addScaledVector(s2, (Math.random() - 0.5) * spread);
    }
    d.normalize();
    // visual kick
    this.model.position.z += 0.025;
    setTimeout(() => { this.model.position.z -= 0.025; }, 70);
    return d;
  }

  // VR sniper scope: render the scene from the scope cam into the lens RT.
  // Costed: only with the sniper in hand, at half frame rate, gun hidden
  // from its own view. VR only (main.js) — desktop/phone aim in the band.
  updateScope(dt, camera, tun) {
    const w = tun.weapons.sniper;
    this.vrScoping = this.current === 'sniper';
    const lens = this.sniper.userData.lens;
    if (!this.vrScoping) return;
    this._rtFrame++;
    if (this._rtFrame % 2) return;
    this.swayT += dt;
    const muzzle = this.muzzle;
    muzzle.getWorldPosition(this.scopeCam.position);
    this.sniper.getWorldQuaternion(this.scopeCam.quaternion);
    // sway: perlin-ish wobble, reduced two-handed
    let sway = (w.sway_deg || 0.3) * Math.PI / 180;
    if (this.twoHanded) sway *= (w.two_hand_sway_mult || 1);
    this.scopeCam.rotateX(Math.sin(this.swayT * 1.7) * sway);
    this.scopeCam.rotateY(Math.sin(this.swayT * 1.3 + 1.6) * sway);
    const wasXr = this.renderer.xr.enabled;
    this.renderer.xr.enabled = false;
    this.model.visible = false;
    lens.visible = false;
    const oldRt = this.renderer.getRenderTarget();
    this.renderer.setRenderTarget(this.rt);
    this.opticPass?.(true);
    // render() never walks the scene (main.js): the RT pass sees this tick's movers
    this.scene.updateMatrixWorld();
    this.renderer.render(this.scene, this.scopeCam);
    this.opticPass?.(false);
    this.renderer.setRenderTarget(oldRt);
    this.model.visible = true;
    lens.visible = true;
    this.renderer.xr.enabled = wasXr;
  }

  // shader warm-up for the optic passes (main.js warmShaders, warmWater, the
  // optic ring). three keys every program on outputColorSpace: a non-XR
  // render target is LinearSRGB, the canvas sRGB, so the RT passes need a
  // second copy of every scene program — the first raise used to compile
  // them all in one frame. monoRt and the sniper rt give the same key, so one
  // pass with monoRt bound covers both. `extra` (a root outside the scene,
  // e.g. terrain's variant keepers, the optic ring) compiles against the
  // scene's fog and lights. The band with the clarity kernel (sharpenTier)
  // renders the scene into _fullRt — same program key as monoRt, so the
  // copies above serve it — and then the quad to the canvas (rt null: tone
  // curve + sRGB out). The VR zoom window's patched lens draws in the canvas
  // pass; it is built (and compiles) on the first zoom.
  // Returns the passes { root, cam, rt, lights } — main.js asks the compile
  // queue for each one (compileq.js want, sliced), never all of them in one task.
  opticPasses(extra) {
    const out = [{ root: this.scene, cam: this.monoCam, rt: this.monoRt, lights: null }];
    if (extra) out.push({ root: extra, cam: this.monoCam, rt: this.monoRt, lights: this.scene });
    // the depth pack (_packDepth) draws into its own target after every
    // optic render that keeps depth
    out.push({ root: this._dPack.scene, cam: this._dPack.cam, rt: this._dPack.rt, lights: null });
    if (this.sharpenTier) out.push({ root: this._full.scene, cam: this._full.cam, rt: null, lights: null });
    return out;
  }

  // this detail tier runs the band through the clarity kernel (an RT +
  // quad) — tier listed in tunables.weapons.monocular.optic.sharpen_tiers.
  // Any other tier draws the band straight to the canvas.
  get sharpenTier() { return this._sharpenTiers.includes(this._tier); }
  // true when THIS frame's band render runs the kernel
  get fullSharpen() { return this.band && this.sharpenTier; }

  // the band's target, sized to the drawing buffer (follows resize and
  // pixel ratio); allocated on first use, kept afterwards
  _fullTarget() {
    const v = this.renderer.getDrawingBufferSize(_fullSize);
    let rt = this._fullRt;
    if (!rt) {
      rt = this._fullRt = new THREE.WebGLRenderTarget(v.x, v.y, { samples: 4, type: this._hdrType,
        depthTexture: _opticDepthTex(this.renderer, 4, v.x, v.y) });
      this._full.uniforms.tDiffuse.value = rt.texture;
    } else if (rt.width !== v.x || rt.height !== v.y) rt.setSize(v.x, v.y);
    this._full.uniforms.uOptTexel.value.set(1 / v.x, 1 / v.y);
    return rt;
  }

  // VR head zoom: the right stick's Y (main.js). Log-rate, push forward =
  // zoom in; back to 1× hides the window. The label rules read the zoom off
  // monoCam.
  get headZMax() { return Math.max(1, this.headVfov / (this.monoBaseFov / this.monoMaxZoom)); }
  get headOn() { return !!(this._head && this._head.win.visible); }
  // A GESTURE = one push of the stick out of its dead zone and back: one that
  // started at 1× and ends under HEAD_Z_MIN snaps back to 1× (a nudge never
  // leaves the window up), and zoomHeadCancel (main.js: the stick click =
  // jump) puts back the zoom the gesture started from (vr-nomap).
  zoomHeadRate(axY, dt) {
    if (this.monoFreeze) return;
    if (Math.abs(axY) < 0.15) {
      if (this._zFrom !== null && this._zFrom <= 1 && this.headZTarget < HEAD_Z_MIN) this.headZTarget = 1;
      this._zFrom = null;
      return;
    }
    if (this._zFrom === null) this._zFrom = this.headZTarget;
    const t = this.headZTarget * Math.exp(-axY * this.headRate * dt);
    this.headZTarget = Math.max(1, Math.min(this.headZMax, t));
  }

  zoomHeadCancel() {
    if (this._zFrom !== null) this.headZTarget = this._zFrom;
    this._zFrom = null;
  }

  // VR exit / combat slot: no window, back to 1×, the overlay blank (a
  // re-open never shows the last session's labels)
  headOff() {
    this.headZ = this.headZTarget = 1;
    this._zFrom = null;
    if (this._head) { this._head.win.visible = false; this._head.drawn = false; }
    this._clearWin();
  }

  _clearWin() {
    this._winCanvas.getContext('2d').clearRect(0, 0, this._winCanvas.width, this._winCanvas.height);
    this._winTex.needsUpdate = true;
  }

  // the window: the optic's lens (its own material — depthTest off, it sits
  // over the world like the panels) + the view-HUD overlay (_winTex), a plain
  // 1.6:1 rectangle at HEAD_D in front of the head (a child of the camera:
  // head-locked)
  // VR: the head zoom window's group (built hidden, parented to the head) — main.js
  // warmSet compiles its lens / overlay behind the black, before the first zoom
  zoomWindow(head) { return this._ensureHead(head).win; }

  _ensureHead(head) {
    if (!this._head) {
      const lensMat = new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false, depthWrite: false, fog: false });
      _patchLens(lensMat, this.monoRt, this._optic);
      const lens = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 1), lensMat);
      const overlay = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 1),
        new THREE.MeshBasicMaterial({ map: this._winTex, transparent: true, depthTest: false,
          depthWrite: false, toneMapped: false, fog: false }));
      lens.renderOrder = 24; overlay.renderOrder = 25;
      lens.frustumCulled = false; overlay.frustumCulled = false;
      overlay.position.z = 0.001;
      const win = new THREE.Group();
      win.add(lens, overlay);
      win.visible = false;
      this._head = { win, lens, overlay, drawn: false };
      this._sizeHead();
    }
    if (this._head.win.parent !== head) head.add(this._head.win);
    return this._head;
  }

  _sizeHead() {
    const h = 2 * HEAD_D * Math.tan((this.headVfov * Math.PI / 180) / 2);
    this._head.win.scale.setScalar(h);
    this._head.win.position.set(0, 0, -HEAD_D);
  }

  // the band, part 1: pose monoCam from the MAIN camera (its zoom came from
  // setBand). Must run before the labels / range are taken, so they project
  // with the exact pose that renders this frame.
  poseMonoFull(dt, camera) {
    if (this.monoFreeze) {               // inspect card open: hold the pose
      this._monoFullAt = performance.now();
      return;
    }
    this.monoCam.aspect = innerWidth / Math.max(1, innerHeight);
    this.monoCam.updateProjectionMatrix();
    camera.getWorldPosition(_monoPos);
    camera.getWorldQuaternion(_monoQ);
    const now = performance.now();
    const snap = now - this._monoFullAt > 500;   // just entered the band
    this._monoFullAt = now;
    if (snap) {
      this.monoCam.quaternion.copy(_monoQ);
      this.monoCam.position.copy(_monoPos);
    } else {
      // image stabilization: hand jitter is magnified by zoom — ease the
      // mono camera toward the view pose. More zoom = steadier glass.
      const k = 1 - Math.exp(-dt * (10 / this.monoZoom));
      this.monoCam.quaternion.slerp(_monoQ, k);
      this.monoCam.position.lerp(_monoPos, k);
    }
    this.monoCam.updateMatrixWorld(true);
    this._monoFrame++;
  }

  // the band, part 2: the main render pass itself, taken through monoCam
  // with the lens dehaze (ramped in, _bandClear). Replaces
  // renderer.render(scene, camera); the HUD canvas is main.js's. On a
  // sharpen tier the scene goes to a drawing-buffer-sized MSAA target and the
  // clarity quad puts it on the canvas (the quad applies the display curve +
  // sRGB out); any other tier renders straight to the canvas.
  renderMonoFull() {
    const r = this.renderer;
    const rt = this.fullSharpen ? this._fullTarget() : null;
    const oldRt = r.getRenderTarget();
    if (rt) r.setRenderTarget(rt);
    const wasVis = this.group.visible;
    this.group.visible = false;            // nothing of the kit in its own view
    // dehazed lens pass: thin the backdrop haze + the near fog, render,
    // restore — naked-eye keeps the full sunny-day atmosphere
    const clear = this._bandClear();
    const fogD = this.scene.fog ? this.scene.fog.density : 0;
    if (this.scene.fog) this.scene.fog.density = fogD * clear;
    setHazeMul(clear);
    // this IS the main pass — but it is taken through monoCam, so the focus
    // wedge is legitimately in force for it
    this.opticPass?.(true);
    r.render(this.scene, this.monoCam);
    this.opticPass?.(false);
    setHazeMul(1);
    if (this.scene.fog) this.scene.fog.density = fogD;
    this.group.visible = wasVis;
    if (rt) {
      this._packDepth(rt);
      // three's state cache is reset before the quad, as LookScale.present
      // (look.js): with a stale cache the quad drew black over the whole band
      // while _fullRt held the image (w62 probe, desktop-igpu, every band fov)
      r.resetState();
      r.setRenderTarget(oldRt);
      r.render(this._full.scene, this._full.cam);
    }
  }

  // VR: true when the NEXT updateMonocular call renders the RT and draws the
  // labels — its own gates, read ahead of it (main.js scopeTarget computes
  // label data only for those frames). Keep in step with updateMonocular.
  monoDrawsNext() {
    return this.current === 'monocular' && !this.monoFreeze
      && (this.headZ > 1 || this.headZTarget > 1) && (this._monoFrame + 1) % this.monoEvery === 0;
  }

  // VR: the head zoom window — the optic posed from the head, its RT on a
  // plain rectangle ahead with the view-HUD overlay (drawLabels). Nothing
  // renders at 1× (the window is hidden). labelData is provided by the
  // caller (main.js _monoLabels: { features, range, markers, labelAlpha }),
  // projected using the EXACT monoCam state that rendered the scene. (main.js
  // calls this in VR only.) `camera` = the head: the window rides it.
  updateMonocular(dt, camera, labelData) {
    if (!this.monoActive) { if (this._head) this.headOff(); return; }
    // freeze-frame: the RT and the label overlay keep the last picture
    if (this.monoFreeze) return;
    // the head zoom: eased in log space every frame, the window up while
    // either end of the ease is past 1×
    this.headZ *= Math.exp((Math.log(this.headZTarget) - Math.log(this.headZ)) * Math.min(1, 8 * dt));
    if (this.headZTarget <= 1 && this.headZ < 1.01) this.headZ = 1;
    const head = this.headZ > 1 || this.headZTarget > 1;
    // up only once it has drawn this showing (never a stale RT / overlay frame)
    if (head || this._head) {
      const h = this._ensureHead(camera);
      if (!head && h.drawn) { h.drawn = false; this._clearWin(); }
      h.win.visible = head && h.drawn;
    }
    if (!head) return;
    this._monoFrame++;
    if (this._monoFrame % this.monoEvery) return;
    this.monoCam.fov = this.headVfov / this.headZ;
    this.monoCam.updateProjectionMatrix();
    camera.getWorldPosition(_monoPos);
    camera.getWorldQuaternion(_monoQ);
    this.monoCam.quaternion.copy(_monoQ);
    this.monoCam.position.copy(_monoPos);
    this.monoCam.updateMatrixWorld(true);
    // no sway — steady observation optic
    const wasXr = this.renderer.xr.enabled;
    this.renderer.xr.enabled = false;
    this._head.win.visible = false;
    const oldRt = this.renderer.getRenderTarget();
    this.renderer.setRenderTarget(this.monoRt);
    // dehazed lens pass: thin the backdrop haze + the near fog, render,
    // restore — the main view keeps the full sunny-day atmosphere
    const fogD = this.scene.fog ? this.scene.fog.density : 0;
    if (this.scene.fog) this.scene.fog.density = fogD * this.monoClear;
    setHazeMul(this.monoClear);
    this.opticPass?.(true);
    this.scene.updateMatrixWorld();   // as updateScope
    this.renderer.render(this.scene, this.monoCam);
    this.opticPass?.(false);
    setHazeMul(1);
    if (this.scene.fog) this.scene.fog.density = fogD;
    this._packDepth(this.monoRt);
    this.renderer.setRenderTarget(oldRt);
    this._head.win.visible = true;
    this._head.drawn = true;
    this.renderer.xr.enabled = wasXr;
    // draw labels NOW, with the exact camera state that produced the RT
    if (labelData) this.drawLabels(labelData);
  }

  // after an optic render into `src` (monoRt, or the band's _fullRt): its
  // depth texture → linear view depth packed into RGBA8 (the pack target, one
  // texel per 4×4 optic px) → an async read (PBO + fence: no CPU/GPU stall,
  // the data lands a few ms later). The snapshot keeps the camera that
  // rendered it, so opticDepth stays exact while the optic moves. A gap of
  // DEPTH_GAP_MS since the last optic render means the optic was lowered: the
  // snapshot of that earlier view is dropped, and a read still in flight
  // from it never lands (_dGen). Leaves the pack target bound; the caller
  // restores its own.
  _packDepth(src) {
    const now = performance.now();
    if (now - this._dLast > DEPTH_GAP_MS) { this._depth = null; this._dGen++; }
    this._dLast = now;
    if (!src.depthTexture || this._dBusy) return;
    const r = this.renderer, p = this._dPack, cam = this.monoCam;
    const w = Math.ceil(src.width / 4), h = Math.ceil(src.height / 4);
    if (p.rt.width !== w || p.rt.height !== h) p.rt.setSize(w, h);
    p.uniforms.tDepth.value = src.depthTexture;
    p.uniforms.uNear.value = cam.near;
    p.uniforms.uFar.value = cam.far;
    r.setRenderTarget(p.rt);
    r.render(p.scene, p.cam);
    const k = this._dNext, s = this._dSnap[k];
    if (!s.buf || s.buf.length !== w * h * 4) s.buf = new Uint8Array(w * h * 4);
    s.w = w; s.h = h; s.far = cam.far;
    s.p0 = cam.projectionMatrix.elements[0];
    s.p5 = cam.projectionMatrix.elements[5];
    s.pv.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
    s.eye.setFromMatrixPosition(cam.matrixWorld);
    this._dBusy = true;
    const gen = this._dGen;
    r.readRenderTargetPixelsAsync(p.rt, 0, 0, w, h, s.buf)
      .then(() => { if (gen === this._dGen) { this._depth = s; this._dNext = k ^ 1; } }, () => {})
      .finally(() => { this._dBusy = false; });
    // three keeps its pack buffer bound until the read lands; a later
    // readPixels must not write into it
    const gl = r.getContext();
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
  }

  // distance (m) from the optic's eye to the RENDERED surface along the ray
  // through overlay px (sx, sy) of monoCam as posed now (the 1024×640 space
  // gazetteer.inView projects into, on every surface), taken from the depth
  // texel (i, j) texels away from the one that ray hits. Infinity = sky (no
  // surface up to the far plane); null = no depth (none landed yet since the
  // optic came up, an optic without a depth texture — phone tier 1 —, or the
  // texel lies outside the snapshot). The ray is reprojected into the
  // snapshot's camera by direction: exact for any rotation since that render,
  // and the eye has moved centimetres, not the metres a 0.97·d test notices.
  opticDepth(sx, sy, i = 0, j = 0) {
    const s = this._depth;
    if (!s) return null;
    const c = this.monoCam;
    _dEye.setFromMatrixPosition(c.matrixWorld);
    _dP.set(sx / 512 - 1, 1 - sy / 320, 1).unproject(c).sub(_dEye).normalize()
      .multiplyScalar(1000).add(s.eye).applyMatrix4(s.pv);
    const tx = Math.floor((_dP.x + 1) * 0.5 * s.w) + i;
    const ty = Math.floor((_dP.y + 1) * 0.5 * s.h) + j;
    if (tx < 0 || ty < 0 || tx >= s.w || ty >= s.h) return null;
    const b = s.buf, o = (ty * s.w + tx) * 4;
    if (b[o] === 255 && b[o + 1] === 255 && b[o + 2] === 255 && b[o + 3] === 255) return Infinity;
    // three's packDepthToRGBA, unpacked: view z / far
    const z = (b[o] / 256 + b[o + 1] / 65536 + b[o + 2] / 16777216 + b[o + 3] / 4278190080) * s.far;
    // view z → distance along the texel centre's ray
    const nx = ((tx + 0.5) / s.w * 2 - 1) / s.p0, ny = ((ty + 0.5) / s.h * 2 - 1) / s.p5;
    return z * Math.sqrt(1 + nx * nx + ny * ny);
  }

  // the zoom window's overlay: ONLY the desktop view HUD's labels, by the
  // same rules (scopehud.js paintZoomWindow; no compass / reticle / range /
  // MAG / rim — vr-nomap). d = main.js _monoLabels { features, labelAlpha }.
  drawLabels(d) {
    paintZoomWindow(this._winCanvas.getContext('2d'), d);
    this._winTex.needsUpdate = true;
  }
}

const _monoQ = new THREE.Quaternion();
const _monoPos = new THREE.Vector3();
const HEAD_D = 1.5;            // the VR head zoom window's distance from the eyes, m
const HEAD_Z_MIN = 1.25;       // a gesture from 1× ending under this snaps back to 1× (no window)
const _fullSize = new THREE.Vector2();
const _dEye = new THREE.Vector3();
const _dP = new THREE.Vector3();

// ------------------------------------------------------------ optic depth
// The labels' line of sight (gazetteer.inView depthAt → opticDepth): the
// depth of the frame the optic actually drew, so a summit hidden behind a
// nearer ridge loses its label whatever the far-height samplers say.
// No optic render for this long = the optic was lowered (or the display was
// off): its last snapshot belongs to another view (_packDepth).
const DEPTH_GAP_MS = 250;

// the optic target's depth attachment, or null (no depth: the label test
// falls back to the march). Single-sample: a plain depth texture. MSAA:
// three resolves depth into it with the colour blit (resolveDepthBuffer,
// default on) — except on a context with WEBGL_multisampled_render_to_texture
// (mobile tilers), which attaches it through that extension, whose depth
// resolve WebGL does not define.
function _opticDepthTex(renderer, samples, w, h) {
  if (samples > 0 && renderer.extensions.has('WEBGL_multisampled_render_to_texture')) return null;
  return new THREE.DepthTexture(w, h);
}

function _depthSnap() {
  return { buf: null, w: 0, h: 0, far: 1, p0: 1, p5: 1,
    pv: new THREE.Matrix4(), eye: new THREE.Vector3() };
}

// the depth pack: the optic's depth texture → view z / far in RGBA8
// (packDepthToRGBA, ~24 bit), all-255 where nothing was drawn (depth 1 = the
// cleared far plane: sky). RGBA8 because it reads back everywhere — a float
// target needs EXT_color_buffer_float, which not every phone has.
function _makeDepthPack() {
  const uniforms = { tDepth: { value: null }, uNear: { value: 0.6 }, uFar: { value: 130000 } };
  const mat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: /* glsl */`
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = vec4( position.xy, 0.0, 1.0 ); }`,
    fragmentShader: /* glsl */`
      #include <packing>
      uniform sampler2D tDepth;
      uniform float uNear, uFar;
      varying vec2 vUv;
      void main() {
        float z = texture2D( tDepth, vUv ).x;
        gl_FragColor = z >= 1.0 ? vec4( 1.0 )
          : packDepthToRGBA( -perspectiveDepthToViewZ( z, uNear, uFar ) / uFar );
      }`,
    depthTest: false,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
  mesh.frustumCulled = false;
  const scene = new THREE.Scene();
  scene.add(mesh);
  const rt = new THREE.WebGLRenderTarget(256, 160, { depthBuffer: false,
    minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
  return { scene, cam: new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1), uniforms, rt };
}

// ------------------------------------------------------------ optic clarity
// Invented "glass" processing on the optic image, linear RGB in, linear out
// (the display curve comes after). Luminance only, in a perceptual space
// (sqrt of linear luminance), so colour and hue stay put:
//  - sharpen: 5-tap unsharp mask (centre vs its 4 neighbours at 1 texel);
//  - clarity: local contrast against a 9-tap mean at uOptClarityR texels
//    (centre + 8 on a ring), weighted toward the midtones so snow and deep
//    shade do not clip or crush.
// Both deltas are soft-limited (d / (1 + k|d|)): fine detail gets the full
// strength, hard edges (a ridge against the sky) only a bounded push — no
// halos. The new luminance scales the pixel's RGB (gain ≤ 4 in linear).
const OPTIC_GLSL = /* glsl */`
uniform float uOptSharpen;
uniform float uOptClarity;
uniform float uOptClarityR;
uniform vec2 uOptTexel;
float optL( vec3 c ) { return sqrt( max( dot( c, vec3( 0.2126, 0.7152, 0.0722 ) ), 0.0 ) ); }
float optSoft( float d, float k ) { return d / ( 1.0 + abs( d ) * k ); }
float optTap( sampler2D t, vec2 uv ) { return optL( texture2D( t, uv ).rgb ); }
vec4 opticClarity( sampler2D t, vec2 uv ) {
  vec4 c = texture2D( t, uv );
  float lc = optL( c.rgb );
  vec2 e = uOptTexel;
  float n4 = optTap( t, uv + vec2( e.x, 0.0 ) ) + optTap( t, uv - vec2( e.x, 0.0 ) )
    + optTap( t, uv + vec2( 0.0, e.y ) ) + optTap( t, uv - vec2( 0.0, e.y ) );
  vec2 r = e * uOptClarityR;
  vec2 q = r * 0.7071;
  float w8 = optTap( t, uv + vec2( r.x, 0.0 ) ) + optTap( t, uv - vec2( r.x, 0.0 ) )
    + optTap( t, uv + vec2( 0.0, r.y ) ) + optTap( t, uv - vec2( 0.0, r.y ) )
    + optTap( t, uv + q ) + optTap( t, uv - q )
    + optTap( t, uv + vec2( q.x, -q.y ) ) + optTap( t, uv + vec2( -q.x, q.y ) );
  float m = clamp( lc, 0.0, 1.0 );
  float l = lc + uOptSharpen * optSoft( lc - 0.25 * n4, 4.0 )
    + uOptClarity * ( 0.4 + 2.4 * m * ( 1.0 - m ) ) * optSoft( lc - ( lc + w8 ) / 9.0, 3.0 );
  float g = lc > 1e-3 ? clamp( l / lc, 0.0, 2.0 ) : 1.0;
  return vec4( c.rgb * ( g * g ), c.a );
}
`;

function _inject(src, anchor, text) {
  if (!src.includes(anchor)) throw new Error(`optic: shader anchor missing: ${anchor}`);
  return src.replace(anchor, text);
}

// the monocular lens: MeshBasicMaterial showing monoRt, its map lookup
// replaced by the clarity kernel. Own program (customProgramCacheKey); the
// label overlay is a separate mesh and never sees the kernel.
function _patchLens(mat, rt, optic) {
  const u = { ...optic, uOptTexel: { value: new THREE.Vector2(1 / rt.width, 1 / rt.height) } };
  mat.map = rt.texture;
  mat.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, u);
    let f = _inject(sh.fragmentShader, 'void main() {', `${OPTIC_GLSL}\nvoid main() {`);
    f = _inject(f, '#include <map_fragment>',
      '#ifdef USE_MAP\n\tdiffuseColor *= opticClarity( map, vMapUv );\n#endif');
    sh.fragmentShader = f;
  };
  mat.customProgramCacheKey = () => 'optic-lens';
  mat.needsUpdate = true;
}

// the band's output pass (desktop/phone): the RT through the clarity
// kernel, then the display curve + sRGB out — the canvas pass's job, which
// the RT render skipped (look.js setupLook patches the tonemapping chunk globally, so
// the curve is the scene's)
function _makeFullQuad(optic) {
  const uniforms = { ...optic, uOptTexel: { value: new THREE.Vector2(1, 1) }, tDiffuse: { value: null } };
  const mat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: /* glsl */`
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = vec4( position.xy, 0.0, 1.0 ); }`,
    fragmentShader: /* glsl */`
      uniform sampler2D tDiffuse;
      varying vec2 vUv;
      ${OPTIC_GLSL}
      void main() {
        gl_FragColor = vec4( opticClarity( tDiffuse, vUv ).rgb, 1.0 );
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
    depthTest: false,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
  mesh.frustumCulled = false;
  const scene = new THREE.Scene();
  scene.add(mesh);
  return { scene, cam: new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1), uniforms };
}

// ---------------------------------------------------------------- models
function gunMat() { return new THREE.MeshLambertMaterial({ color: 0x2b2f36 }); }
function darkMat() { return new THREE.MeshLambertMaterial({ color: 0x17191d }); }

function makeRifle() {
  const g = new THREE.Group();
  const m = gunMat();
  const body = new THREE.Mesh(new THREE.BoxGeometry(0.045, 0.12, 0.42), m);
  body.position.set(0, -0.02, -0.12);
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.24, 8), darkMat());
  barrel.rotation.x = Math.PI / 2;
  barrel.position.set(0, 0.03, -0.39);
  const grip = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.11, 0.05), m);
  grip.position.set(0, -0.1, 0.02);
  const fore = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.05, 0.14), m);
  fore.position.set(0, -0.045, -0.3);
  const sightF = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.03, 0.006), darkMat());
  sightF.position.set(0, 0.075, -0.48);
  const sightR = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.025, 0.006), darkMat());
  sightR.position.set(0, 0.072, -0.02);
  g.add(body, barrel, grip, fore, sightF, sightR);
  g.userData.muzzle = new THREE.Object3D();
  g.userData.muzzle.position.set(0, 0.03, -0.52);
  g.add(g.userData.muzzle);
  g.userData.foreGripPos = new THREE.Vector3(0, -0.045, -0.3);
  return g;
}

function makeSniper() {
  const g = new THREE.Group();
  const m = gunMat();
  const body = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.11, 0.62), m);
  body.position.set(0, -0.02, -0.16);
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.014, 0.4, 8), darkMat());
  barrel.rotation.x = Math.PI / 2;
  barrel.position.set(0, 0.02, -0.62);
  const grip = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.12, 0.05), m);
  grip.position.set(0, -0.11, 0.06);
  const stock = new THREE.Mesh(new THREE.BoxGeometry(0.045, 0.09, 0.16), m);
  stock.position.set(0, -0.04, 0.22);
  // scope tube + lens (RT display)
  const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 0.2, 12), darkMat());
  tube.rotation.x = Math.PI / 2;
  tube.position.set(0, 0.075, -0.1);
  const lens = new THREE.Mesh(new THREE.CircleGeometry(0.026, 24),
    new THREE.MeshBasicMaterial({ color: 0xffffff }));
  lens.position.set(0, 0.075, 0.001);
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.026, 0.033, 24),
    new THREE.MeshBasicMaterial({ color: 0x0a0a0a, side: THREE.DoubleSide }));
  ring.position.copy(lens.position);
  // reticle: thin cross + centre dot drawn just in front of the RT lens
  // (children of the group, so the scope's own render pass hides them with
  // the rest of the model — the reticle never appears inside its own view)
  const retMat = new THREE.MeshBasicMaterial({ color: 0x111111 });
  const retH = new THREE.Mesh(new THREE.PlaneGeometry(0.046, 0.0014), retMat);
  const retV = new THREE.Mesh(new THREE.PlaneGeometry(0.0014, 0.046), retMat);
  const retDot = new THREE.Mesh(new THREE.CircleGeometry(0.0022, 10),
    new THREE.MeshBasicMaterial({ color: 0xcc2222 }));
  for (const r of [retH, retV, retDot]) {
    r.position.copy(lens.position);
    r.position.z += 0.0015;
    g.add(r);
  }
  g.add(body, barrel, grip, stock, tube, lens, ring);
  g.userData.muzzle = new THREE.Object3D();
  g.userData.muzzle.position.set(0, 0.02, -0.83);
  g.add(g.userData.muzzle);
  g.userData.lens = lens;
  g.userData.foreGripPos = new THREE.Vector3(0, -0.04, -0.42);
  return g;
}

// the monocular slot: no model (owner 2026-11-01: no monocular / binocular
// in VR; desktop / phone never drew it) — an empty group with the muzzle and
// fore grip every slot carries
function makeMonocular() {
  const g = new THREE.Group();
  g.userData.muzzle = new THREE.Object3D();
  g.userData.muzzle.position.set(0, 0, -0.2);
  g.add(g.userData.muzzle);
  g.userData.foreGripPos = new THREE.Vector3(0, -0.02, -0.1);
  return g;
}

// fireDir spread scratch (no per-shot allocation)
const _spreadUp = new THREE.Vector3();
const _spread1 = new THREE.Vector3();
const _spread2 = new THREE.Vector3();
